import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiArchive,
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiImage,
  FiLayers,
  FiPackage,
  FiPlus,
  FiTrash2,
  FiUpload,
  FiX,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../services/api";

const steps = [
  { title: "Product Details", icon: <FiPackage /> },
  { title: "Variants", icon: <FiLayers /> },
  { title: "Lot Pricing", icon: <FiArchive /> },
  { title: "Inventory", icon: <FiArchive /> },
  { title: "Images", icon: <FiImage /> },
  { title: "Review", icon: <FiCheckCircle /> },
];

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatRuleGroup = (value) =>
  String(value || "APPAREL")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatValueBand = (minimumValue, maximumValue) => {
  const minimum =
    minimumValue === null || minimumValue === undefined
      ? null
      : Number(minimumValue);

  const maximum =
    maximumValue === null || maximumValue === undefined
      ? null
      : Number(maximumValue);

  if (minimum !== null && maximum !== null) {
    return `${formatMoney(minimum)} – ${formatMoney(maximum)}`;
  }

  if (minimum !== null) {
    return `${formatMoney(minimum)} and above`;
  }

  if (maximum !== null) {
    return `Up to ${formatMoney(maximum)}`;
  }

  return "All item values";
};

const buildGstRuleGroups = (slabs) =>
  Object.values(
    (slabs || []).reduce((groups, slab) => {
      const ruleGroup = slab.ruleGroup || "GENERAL";
      groups[ruleGroup] ||= { ruleGroup, bands: [] };
      groups[ruleGroup].bands.push(slab);
      return groups;
    }, {}),
  )
    .map((group) => ({
      ...group,
      bands: [...group.bands].sort(
        (left, right) =>
          Number(left.minimumItemValue ?? 0) -
          Number(right.minimumItemValue ?? 0),
      ),
    }))
    .sort((left, right) => left.ruleGroup.localeCompare(right.ruleGroup));

const formatGstBands = (bands) =>
  bands
    .map(
      (slab) =>
        `Purchase ${formatValueBand(slab.minimumItemValue, slab.maximumItemValue)} · Sale ${formatValueBand(slab.saleMinimumItemValue, slab.saleMaximumItemValue)}: ${Number(slab.rate).toFixed(2)}%`,
    )
    .join(" · ");

const emptyVariant = () => ({
  combinationKey: "DEFAULT",
  variantName: "Default",
  sku: "",
  supplierBarcode: "",
  colorId: "",
  sizeId: "",
  isActive: true,
});

const initialForm = {
  name: "",
  productCode: "",
  slug: "",
  status: "DRAFT",
  brandId: "",
  categoryId: "",
  subCategoryId: "",
  unitId: "",
  seasonId: "",
  hsnCodeId: "",
  gstSlabId: "",
  description: "",
  shortDescription: "",
  fullDescription: "",
  trackInventory: true,
  allowNegativeStock: false,
  autoReorderAlerts: true,
  variantLevelTracking: true,
  isFeatured: false,
  isNewArrival: false,
  isBestSeller: false,
  isOnlineVisible: false,
  allowReviews: true,
};

const optionalText = (value) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const getAssetUrl = (storageKey) => {
  if (!storageKey) return "";

  if (storageKey.startsWith("http://") || storageKey.startsWith("https://")) {
    return storageKey;
  }

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return `${baseUrl}${storageKey.startsWith("/") ? "" : "/"}${storageKey}`;
};

export default function ProductForm() {
  const navigate = useNavigate();
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [variants, setVariants] = useState([emptyVariant()]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierToAdd, setSupplierToAdd] = useState("");
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [masters, setMasters] = useState({
    brands: [],
    categories: [],
    subcategories: [],
    units: [],
    seasons: [],
    suppliers: [],
    colors: [],
    sizes: [],
    hsnCodes: [],
    gstSlabs: [],
  });

  const [loadingMasters, setLoadingMasters] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedGstSlab = useMemo(
    () => masters.gstSlabs.find((slab) => slab.id === form.gstSlabId) || null,
    [form.gstSlabId, masters.gstSlabs],
  );

  const gstRuleGroups = useMemo(
    () => buildGstRuleGroups(masters.gstSlabs),
    [masters.gstSlabs],
  );

  const selectedGstRuleGroup = useMemo(
    () =>
      gstRuleGroups.find(
        (group) => group.ruleGroup === selectedGstSlab?.ruleGroup,
      ) || null,
    [gstRuleGroups, selectedGstSlab],
  );

  const loadMasters = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoadingMasters(true);
      setError("");

      const [
        brands,
        categories,
        subcategories,
        units,
        seasons,
        suppliersData,
        colors,
        sizes,
        hsnCodes,
        gstSlabs,
      ] = await Promise.all([
        apiRequest("/brands", { accessToken }),
        apiRequest("/categories", { accessToken }),
        apiRequest("/subcategories", { accessToken }),
        apiRequest("/units", { accessToken }),
        apiRequest("/seasons", { accessToken }),
        apiRequest("/suppliers", { accessToken }),
        apiRequest("/colors", { accessToken }),
        apiRequest("/sizes", { accessToken }),
        apiRequest("/tax-catalog/hsn-codes?activeOnly=true", {
          accessToken,
        }),
        apiRequest("/tax-catalog/gst-slabs?activeOnly=true", {
          accessToken,
        }),
      ]);

      setMasters({
        brands: brands || [],
        categories: categories || [],
        subcategories: subcategories || [],
        units: units || [],
        seasons: seasons || [],
        suppliers: suppliersData || [],
        colors: colors || [],
        sizes: sizes || [],
        hsnCodes: hsnCodes || [],
        gstSlabs: gstSlabs || [],
      });
    } catch (err) {
      setError(err.message || "Unable to load product master data.");
    } finally {
      setLoadingMasters(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadMasters();
  }, [isAuthLoading, accessToken, loadMasters]);

  const filteredSubcategories = useMemo(() => {
    if (!form.categoryId) return masters.subcategories;

    return masters.subcategories.filter(
      (subcategory) => subcategory.categoryId === form.categoryId,
    );
  }, [form.categoryId, masters.subcategories]);

  const selectedSubcategory = useMemo(
    () =>
      masters.subcategories.find(
        (subcategory) => subcategory.id === form.subCategoryId,
      ),
    [form.subCategoryId, masters.subcategories],
  );

  const filteredSizes = useMemo(() => {
    const sizeGroupId = selectedSubcategory?.sizeGroupId;

    if (!sizeGroupId) return masters.sizes;

    return masters.sizes.filter((size) => size.sizeGroupId === sizeGroupId);
  }, [masters.sizes, selectedSubcategory]);

  const updateForm = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateVariant = (index, field, value) => {
    setVariants((previous) =>
      previous.map((variant, variantIndex) => {
        if (variantIndex !== index) return variant;

        const updated = {
          ...variant,
          [field]: value,
        };

        if (field === "colorId" || field === "sizeId") {
          const color = masters.colors.find(
            (item) => item.id === updated.colorId,
          );

          const size = masters.sizes.find((item) => item.id === updated.sizeId);

          const nameParts = [color?.name, size?.name].filter(Boolean);

          updated.variantName =
            nameParts.length > 0 ? nameParts.join(" / ") : "Default";

          updated.combinationKey =
            nameParts.length > 0
              ? `${updated.colorId || "NO-COLOR"}-${updated.sizeId || "NO-SIZE"}`
              : "DEFAULT";
        }

        return updated;
      }),
    );
  };

  const addVariant = () => {
    setVariants((previous) => [
      ...previous,
      {
        ...emptyVariant(),
        combinationKey: `VARIANT-${previous.length + 1}`,
        variantName: `Variant ${previous.length + 1}`,
      },
    ]);
  };

  const removeVariant = (index) => {
    if (variants.length === 1) {
      setError("A product must have at least one variant.");
      return;
    }

    setVariants((previous) =>
      previous.filter((_, variantIndex) => variantIndex !== index),
    );
  };

  const addSupplier = () => {
    if (!supplierToAdd) return;

    const exists = suppliers.some(
      (supplier) => supplier.supplierId === supplierToAdd,
    );

    if (exists) {
      setError("This supplier is already linked to the product.");
      return;
    }

    setSuppliers((previous) => [
      ...previous,
      {
        supplierId: supplierToAdd,
        isPreferred: previous.length === 0,
        supplierSku: "",
      },
    ]);

    setSupplierToAdd("");
    setError("");
  };

  const updateSupplier = (supplierId, field, value) => {
    setSuppliers((previous) =>
      previous.map((supplier) => {
        if (supplier.supplierId !== supplierId) {
          return field === "isPreferred" && value
            ? { ...supplier, isPreferred: false }
            : supplier;
        }

        return {
          ...supplier,
          [field]: value,
        };
      }),
    );
  };

  const removeSupplier = (supplierId) => {
    setSuppliers((previous) =>
      previous.filter((supplier) => supplier.supplierId !== supplierId),
    );
  };

  const uploadSelectedImages = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (!selectedFiles.length) return;

    if (images.length + selectedFiles.length > 8) {
      setError("You can upload a maximum of 8 product images.");
      event.target.value = "";
      return;
    }

    try {
      setUploadingImages(true);
      setError("");

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("file", file);

        const uploaded = await apiRequest("/uploads/products", {
          method: "POST",
          body: formData,
          accessToken,
        });

        setImages((previous) => [
          ...previous,
          {
            id: uploaded.storageKey,
            storageKey: uploaded.storageKey,
            altText: "",
            role: previous.length === 0 ? "THUMBNAIL" : "GALLERY",
            sortOrder: previous.length,
          },
        ]);
      }
    } catch (err) {
      setError(err.message || "Unable to upload product image.");
    } finally {
      setUploadingImages(false);
      event.target.value = "";
    }
  };

  const removeImage = (storageKey) => {
    setImages((previous) => {
      const removedImage = previous.find(
        (image) => image.storageKey === storageKey,
      );

      const remaining = previous.filter(
        (image) => image.storageKey !== storageKey,
      );

      return remaining.map((image, index) => ({
        ...image,
        role:
          removedImage?.role === "THUMBNAIL" && index === 0
            ? "THUMBNAIL"
            : image.role,
        sortOrder: index,
      }));
    });
  };

  const makeThumbnail = (storageKey) => {
    setImages((previous) =>
      previous.map((image) => ({
        ...image,
        role:
          image.storageKey === storageKey
            ? "THUMBNAIL"
            : image.role === "THUMBNAIL"
              ? "GALLERY"
              : image.role,
      })),
    );
  };

  const updateImageAltText = (storageKey, altText) => {
    setImages((previous) =>
      previous.map((image) =>
        image.storageKey === storageKey ? { ...image, altText } : image,
      ),
    );
  };

  const validateCurrentStep = () => {
    setError("");

    if (step === 1) {
      if (!form.name.trim()) {
        setError("Product name is required.");
        return false;
      }

      if (!form.unitId) {
        setError("Please select a product unit.");
        return false;
      }

      if (!form.hsnCodeId) {
        setError("Please select an HSN code.");
        return false;
      }

      if (!form.gstSlabId) {
        setError("Please select a GST slab.");
        return false;
      }
    }

    if (step === 2) {
      const hasEmptySku = variants.some((variant) => !variant.sku.trim());

      if (hasEmptySku) {
        setError("Every variant must have an SKU.");
        return false;
      }

      const skus = variants.map((variant) => variant.sku.trim().toUpperCase());

      if (new Set(skus).size !== skus.length) {
        setError("Every variant must have a unique SKU.");
        return false;
      }

      const combinations = variants.map((variant) => variant.combinationKey);

      if (new Set(combinations).size !== combinations.length) {
        setError("Every variant must have a unique color/size combination.");
        return false;
      }
    }

    if (step === 5 && form.isOnlineVisible && images.length === 0) {
      setError(
        "Add at least one image before making this product visible online.",
      );
      return false;
    }

    return true;
  };

  const nextStep = () => {
    if (!validateCurrentStep()) return;

    setStep((current) => Math.min(current + 1, steps.length));
  };

  const previousStep = () => {
    setError("");
    setStep((current) => Math.max(current - 1, 1));
  };

  const createProduct = async () => {
    if (!validateCurrentStep()) return;

    try {
      setSaving(true);
      setError("");

      const payload = {
        name: form.name.trim(),
        productCode: optionalText(form.productCode)?.toUpperCase(),
        slug: optionalText(form.slug),
        status: form.status,
        brandId: form.brandId || undefined,
        categoryId: form.categoryId || undefined,
        subCategoryId: form.subCategoryId || undefined,
        unitId: form.unitId,
        seasonId: form.seasonId || undefined,
        hsnCodeId: form.hsnCodeId,
        gstSlabId: form.gstSlabId,
        description: optionalText(form.description),
        shortDescription: optionalText(form.shortDescription),
        fullDescription: optionalText(form.fullDescription),
        trackInventory: form.trackInventory,
        allowNegativeStock: form.allowNegativeStock,
        autoReorderAlerts: form.autoReorderAlerts,
        variantLevelTracking: form.variantLevelTracking,
        isFeatured: form.isFeatured,
        isNewArrival: form.isNewArrival,
        isBestSeller: form.isBestSeller,
        isOnlineVisible: form.isOnlineVisible,
        allowReviews: form.allowReviews,

        variants: variants.map((variant) => ({
          combinationKey: variant.combinationKey,
          variantName: optionalText(variant.variantName),
          sku: variant.sku.trim().toUpperCase(),
          supplierBarcode: optionalText(variant.supplierBarcode),
          colorId: variant.colorId || undefined,
          sizeId: variant.sizeId || undefined,
          isActive: variant.isActive,
        })),

        suppliers: suppliers.map((supplier) => ({
          supplierId: supplier.supplierId,
          isPreferred: supplier.isPreferred,
          supplierSku: optionalText(supplier.supplierSku),
        })),

        images: images.map((image, index) => ({
          storageKey: image.storageKey,
          role: image.role,
          altText: optionalText(image.altText),
          sortOrder: index,
        })),
      };

      const product = await apiRequest("/products", {
        method: "POST",
        body: payload,
        accessToken,
      });

      navigate(`/admindashboard/masters/products/${product.id}`);
    } catch (err) {
      setError(err.message || "Unable to create product.");
    } finally {
      setSaving(false);
    }
  };

  if (loadingMasters || isAuthLoading) {
    return <div className="py-12 text-center text-gray-500">Loading form…</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <p className="text-sm text-gray-500">
          Masters / Products / Add Product
        </p>

        <h1 className="mt-1 text-3xl font-bold text-gray-900">
          Create Product
        </h1>

        <p className="mt-2 text-gray-500">
          Add product details, variants, prices, inventory settings, and product
          images.
        </p>
      </div>

      <div className="mb-8 rounded-3xl border border-gray-100 bg-white p-6">
        <div className="grid grid-cols-3 gap-4 md:grid-cols-6">
          {steps.map((item, index) => {
            const current = index + 1;

            return (
              <button
                type="button"
                key={item.title}
                onClick={() => {
                  if (current <= step) setStep(current);
                }}
                className="flex flex-col items-center text-center"
              >
                <div
                  className={`mb-3 flex h-12 w-12 items-center justify-center rounded-2xl text-lg ${
                    current <= step
                      ? "bg-primary-700 text-white"
                      : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {item.icon}
                </div>

                <p
                  className={`text-xs font-medium md:text-sm ${
                    current <= step ? "text-primary-700" : "text-gray-500"
                  }`}
                >
                  {item.title}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white">
        <div className="p-6 md:p-8">
          {error && (
            <div className="mb-6 rounded-xl bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="mb-6 text-xl font-bold">Product Details</h2>

              <div className="grid gap-5 md:grid-cols-2">
                <Field
                  label="Product Name *"
                  value={form.name}
                  onChange={(value) => updateForm("name", value)}
                  placeholder="Nike Polo T-Shirt"
                />

                <Field
                  label="Product Code"
                  value={form.productCode}
                  onChange={(value) =>
                    updateForm("productCode", value.toUpperCase())
                  }
                  placeholder="NIKE-POLO"
                />

                <Field
                  label="URL Slug (auto-generated if blank)"
                  value={form.slug}
                  onChange={(value) => updateForm("slug", value)}
                  placeholder="nike-polo-tshirt"
                />

                <SelectField
                  label="Status"
                  value={form.status}
                  onChange={(value) => updateForm("status", value)}
                  options={[
                    ["DRAFT", "Draft"],
                    ["ACTIVE", "Active"],
                    ["INACTIVE", "Inactive"],
                  ]}
                />

                <SelectField
                  label="Brand"
                  value={form.brandId}
                  onChange={(value) => updateForm("brandId", value)}
                  placeholder="Select brand"
                  options={masters.brands.map((brand) => [
                    brand.id,
                    brand.name,
                  ])}
                />

                <SelectField
                  label="Category"
                  value={form.categoryId}
                  onChange={(value) => {
                    updateForm("categoryId", value);
                    updateForm("subCategoryId", "");
                  }}
                  placeholder="Select category"
                  options={masters.categories.map((category) => [
                    category.id,
                    category.name,
                  ])}
                />

                <SelectField
                  label="Subcategory"
                  value={form.subCategoryId}
                  onChange={(value) => updateForm("subCategoryId", value)}
                  placeholder="Select subcategory"
                  options={filteredSubcategories.map((subcategory) => [
                    subcategory.id,
                    subcategory.name,
                  ])}
                />

                <SelectField
                  label="Unit *"
                  value={form.unitId}
                  onChange={(value) => updateForm("unitId", value)}
                  placeholder="Select unit"
                  options={masters.units.map((unit) => [
                    unit.id,
                    `${unit.name}${unit.symbol ? ` (${unit.symbol})` : ""}`,
                  ])}
                />

                <SelectField
                  label="Season"
                  value={form.seasonId}
                  onChange={(value) => updateForm("seasonId", value)}
                  placeholder="Select season"
                  options={masters.seasons.map((season) => [
                    season.id,
                    season.name,
                  ])}
                />

                <SelectField
                  label="HSN Code *"
                  value={form.hsnCodeId}
                  onChange={(value) => updateForm("hsnCodeId", value)}
                  placeholder="Select HSN code"
                  options={masters.hsnCodes.map((hsn) => [
                    hsn.id,
                    hsn.description
                      ? `${hsn.code} — ${hsn.description}`
                      : hsn.code,
                  ])}
                />

                <div className="space-y-2">
                  <SelectField
                    label="GST Rule Group *"
                    value={selectedGstRuleGroup?.ruleGroup || ""}
                    onChange={(ruleGroup) => {
                      const group = gstRuleGroups.find(
                        (item) => item.ruleGroup === ruleGroup,
                      );
                      updateForm("gstSlabId", group?.bands[0]?.id || "");
                    }}
                    placeholder="Select GST rule group"
                    options={gstRuleGroups.map((group) => [
                      group.ruleGroup,
                      `${formatRuleGroup(group.ruleGroup)} · ${group.bands.length} automatic band${group.bands.length === 1 ? "" : "s"}`,
                    ])}
                  />

                  <div className="rounded-xl border border-primary-100 bg-primary-50 px-4 py-3 text-xs text-primary-900">
                    <p className="font-semibold">
                      GST is calculated automatically at billing.
                    </p>

                    <p className="mt-1 leading-5 text-primary-800">
                      The final discounted value of each item decides which GST
                      rate applies. Do not enter a GST percentage manually on
                      the product.
                    </p>

                    {selectedGstRuleGroup && (
                      <p className="mt-2 font-medium text-primary-900">
                        Selected group: {formatRuleGroup(selectedGstRuleGroup.ruleGroup)} ·{" "}
                        {formatGstBands(selectedGstRuleGroup.bands)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium">
                    Short Description
                  </label>

                  <textarea
                    rows="2"
                    value={form.shortDescription}
                    onChange={(event) =>
                      updateForm("shortDescription", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-700"
                    placeholder="Short customer-facing product description."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium">
                    Description
                  </label>

                  <textarea
                    rows="5"
                    value={form.description}
                    onChange={(event) =>
                      updateForm("description", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-700"
                    placeholder="Complete product description."
                  />
                </div>
              </div>

              <div className="mt-8 border-t pt-6">
                <h3 className="font-semibold">Suppliers</h3>

                <p className="mt-1 text-sm text-gray-500">
                  Link one or more suppliers. Only one supplier can be
                  preferred.
                </p>

                <div className="mt-4 flex flex-col gap-3 md:flex-row">
                  <select
                    value={supplierToAdd}
                    onChange={(event) => setSupplierToAdd(event.target.value)}
                    className="h-12 flex-1 rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
                  >
                    <option value="">Select supplier</option>

                    {masters.suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.displayName || supplier.name}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={addSupplier}
                    className="flex h-12 items-center justify-center gap-2 rounded-xl bg-primary-700 px-5 text-white hover:bg-primary-800"
                  >
                    <FiPlus />
                    Add Supplier
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  {suppliers.map((linkedSupplier) => {
                    const supplier = masters.suppliers.find(
                      (item) => item.id === linkedSupplier.supplierId,
                    );

                    return (
                      <div
                        key={linkedSupplier.supplierId}
                        className="grid items-center gap-3 rounded-xl border border-gray-100 p-4 md:grid-cols-[1fr_180px_120px_40px]"
                      >
                        <p className="font-medium">
                          {supplier?.displayName || supplier?.name}
                        </p>

                        <input
                          value={linkedSupplier.supplierSku}
                          onChange={(event) =>
                            updateSupplier(
                              linkedSupplier.supplierId,
                              "supplierSku",
                              event.target.value,
                            )
                          }
                          placeholder="Supplier SKU"
                          className="h-10 rounded-lg border border-gray-200 px-3 outline-none focus:border-primary-700"
                        />

                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={linkedSupplier.isPreferred}
                            onChange={(event) =>
                              updateSupplier(
                                linkedSupplier.supplierId,
                                "isPreferred",
                                event.target.checked,
                              )
                            }
                          />
                          Preferred
                        </label>

                        <button
                          type="button"
                          onClick={() =>
                            removeSupplier(linkedSupplier.supplierId)
                          }
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                          aria-label="Remove supplier"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Variants</h2>

                  <p className="mt-1 text-sm text-gray-500">
                    Every product needs at least one sellable variant and SKU.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={addVariant}
                  className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-4 text-white hover:bg-primary-800"
                >
                  <FiPlus />
                  Add Variant
                </button>
              </div>

              <div className="space-y-5">
                {variants.map((variant, index) => (
                  <div
                    key={index}
                    className="rounded-2xl border border-gray-200 p-5"
                  >
                    <div className="mb-5 flex items-center justify-between">
                      <h3 className="font-semibold">Variant {index + 1}</h3>

                      <button
                        type="button"
                        onClick={() => removeVariant(index)}
                        className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                        aria-label="Remove variant"
                      >
                        <FiTrash2 />
                      </button>
                    </div>

                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                      <SelectField
                        label="Color"
                        value={variant.colorId}
                        onChange={(value) =>
                          updateVariant(index, "colorId", value)
                        }
                        placeholder="No color"
                        options={masters.colors.map((color) => [
                          color.id,
                          color.name,
                        ])}
                      />

                      <SelectField
                        label="Size"
                        value={variant.sizeId}
                        onChange={(value) =>
                          updateVariant(index, "sizeId", value)
                        }
                        placeholder="No size"
                        options={filteredSizes.map((size) => [
                          size.id,
                          size.name,
                        ])}
                      />

                      <Field
                        label="Variant Name"
                        value={variant.variantName}
                        onChange={(value) =>
                          updateVariant(index, "variantName", value)
                        }
                        placeholder="Black / Large"
                      />

                      <Field
                        label="SKU *"
                        value={variant.sku}
                        onChange={(value) =>
                          updateVariant(index, "sku", value.toUpperCase())
                        }
                        placeholder="NIKE-POLO-BLK-L"
                      />

                      <div>
                        <label className="mb-2 block text-sm font-medium">
                          CHARMS barcode
                        </label>

                        <div className="flex h-12 items-center rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-500">
                          Generated automatically after product creation
                        </div>
                      </div>

                      <Field
                        label="Supplier / EAN barcode"
                        value={variant.supplierBarcode || ""}
                        onChange={(value) =>
                          updateVariant(index, "supplierBarcode", value)
                        }
                        placeholder="Optional supplier barcode"
                      />

                      <div>
                        <label className="mb-2 block text-sm font-medium">
                          Combination Key
                        </label>

                        <input
                          readOnly
                          value={variant.combinationKey}
                          className="h-12 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="mb-2 text-xl font-bold">Lot Pricing</h2>

              <p className="mb-6 text-sm text-gray-500">
                Prices and cost are set when stock is received through a goods
                receipt note (GRN), never in the product catalogue.
              </p>

              <div className="rounded-3xl border border-primary-100 bg-primary-50 p-6">
                <h3 className="text-lg font-semibold text-primary-950">
                  Create the catalogue first, then receive stock by lot
                </h3>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-primary-900/80">
                  Each GRN creates a purchase lot with its own cost, MRP, POS
                  price, online price, and available quantity. This preserves
                  the correct selling price and margin when the same product is
                  received again at a different cost.
                </p>

                <ol className="mt-5 list-decimal space-y-2 pl-5 text-sm text-primary-950">
                  <li>Create this product and its variants.</li>
                  <li>Create and approve a purchase order.</li>
                  <li>
                    Post its GRN and enter the lot's MRP, POS price, online
                    price, and default online-lot setting.
                  </li>
                </ol>
              </div>

              <div className="mt-6 space-y-4">
                {variants.map((variant, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-3 rounded-2xl border border-gray-200 p-5 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <h3 className="font-semibold">
                      {variant.variantName || `Variant ${index + 1}`} —{" "}
                      {variant.sku || "SKU not entered"}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        No catalogue price. Add lot pricing from the GRN after
                        this product is created.
                      </p>
                    </div>

                    <span className="w-fit rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                      {variant.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="mb-2 text-xl font-bold">Inventory Settings</h2>

              <p className="mb-6 text-sm text-gray-500">
                Add opening stock after creating the product from the Inventory
                module. This keeps the stock ledger accurate.
              </p>

              <div className="grid gap-4 md:grid-cols-2">
                <Toggle
                  label="Track inventory"
                  description="Maintain stock balances for product variants."
                  checked={form.trackInventory}
                  onChange={(value) => updateForm("trackInventory", value)}
                />

                <Toggle
                  label="Allow negative stock"
                  description="Permit stock movements below zero."
                  checked={form.allowNegativeStock}
                  onChange={(value) => updateForm("allowNegativeStock", value)}
                />

                <Toggle
                  label="Automatic reorder alerts"
                  description="Show alerts when a store reaches its reorder level."
                  checked={form.autoReorderAlerts}
                  onChange={(value) => updateForm("autoReorderAlerts", value)}
                />

                <Toggle
                  label="Variant-level tracking"
                  description="Track inventory separately for each color and size."
                  checked={form.variantLevelTracking}
                  onChange={(value) =>
                    updateForm("variantLevelTracking", value)
                  }
                />
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-xl font-bold">Product Images</h2>

                  <p className="mt-1 text-sm text-gray-500">
                    Upload up to 8 JPG, PNG, or WEBP images. The main image is
                    used on the website and ERP product cards.
                  </p>
                </div>

                <span className="w-fit rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-700">
                  {images.length}/8 images
                </span>
              </div>

              <label
                className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center transition ${
                  uploadingImages
                    ? "cursor-wait border-primary-200 bg-primary-50"
                    : "cursor-pointer border-gray-300 bg-gray-50 hover:border-primary-500 hover:bg-primary-50"
                }`}
              >
                <FiUpload className="mb-3 text-primary-700" size={32} />

                <p className="font-semibold text-gray-800">
                  {uploadingImages
                    ? "Uploading images…"
                    : "Click here to choose product images"}
                </p>

                <p className="mt-1 text-sm text-gray-500">
                  JPG, PNG, or WEBP · Maximum 5 MB per image
                </p>

                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  disabled={uploadingImages || images.length >= 8}
                  onChange={uploadSelectedImages}
                  className="hidden"
                />
              </label>

              {images.length > 0 && (
                <div className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {images.map((image, index) => (
                    <div
                      key={image.storageKey}
                      className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
                    >
                      <div className="relative aspect-square bg-gray-100">
                        <img
                          src={getAssetUrl(image.storageKey)}
                          alt={image.altText || `Product image ${index + 1}`}
                          className="h-full w-full object-cover"
                        />

                        {image.role === "THUMBNAIL" && (
                          <span className="absolute left-3 top-3 rounded-full bg-primary-700 px-3 py-1 text-xs font-semibold text-white">
                            Main image
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={() => removeImage(image.storageKey)}
                          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-red-600 shadow hover:bg-red-50"
                          aria-label="Remove image"
                        >
                          <FiX />
                        </button>
                      </div>

                      <div className="space-y-3 p-4">
                        <input
                          value={image.altText}
                          onChange={(event) =>
                            updateImageAltText(
                              image.storageKey,
                              event.target.value,
                            )
                          }
                          placeholder="Image description (optional)"
                          className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary-700"
                        />

                        <button
                          type="button"
                          disabled={image.role === "THUMBNAIL"}
                          onClick={() => makeThumbnail(image.storageKey)}
                          className="w-full rounded-lg border border-primary-200 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-default disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                          {image.role === "THUMBNAIL"
                            ? "Main product image"
                            : "Set as main image"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 6 && (
            <div>
              <h2 className="mb-6 text-xl font-bold">Review Product</h2>

              <div className="grid gap-5 md:grid-cols-2">
                <ReviewItem label="Product Name" value={form.name} />

                <ReviewItem
                  label="Product Code"
                  value={form.productCode || "—"}
                />

                <ReviewItem
                  label="Brand"
                  value={
                    masters.brands.find((item) => item.id === form.brandId)
                      ?.name || "—"
                  }
                />

                <ReviewItem
                  label="Category"
                  value={
                    masters.categories.find(
                      (item) => item.id === form.categoryId,
                    )?.name || "—"
                  }
                />

                <ReviewItem
                  label="HSN Code"
                  value={
                    masters.hsnCodes.find((item) => item.id === form.hsnCodeId)
                      ?.code || "—"
                  }
                />

                <ReviewItem
                  label="GST Rule Group"
                  value={
                    selectedGstSlab
                      ? `${formatRuleGroup(
                          selectedGstSlab.ruleGroup,
                        )} · Automatic item-value GST`
                      : "—"
                  }
                />

                <ReviewItem
                  label="Automatic GST Bands"
                  value={
                    selectedGstRuleGroup
                      ? formatGstBands(selectedGstRuleGroup.bands)
                      : "—"
                  }
                />

                <ReviewItem label="Variants" value={variants.length} />
                <ReviewItem label="Suppliers" value={suppliers.length} />
                <ReviewItem label="Images" value={images.length} />

                <ReviewItem
                  label="Inventory Tracking"
                  value={form.trackInventory ? "Enabled" : "Disabled"}
                />

                <ReviewItem label="Status" value={form.status} />
              </div>

              <div className="mt-6 rounded-2xl bg-gray-50 p-5">
                <h3 className="mb-3 font-semibold">Variants to create</h3>

                <div className="space-y-2">
                  {variants.map((variant, index) => (
                    <div
                      key={index}
                      className="flex flex-wrap justify-between gap-2 text-sm text-gray-700"
                    >
                      <span>
                        {variant.variantName || "Default"} · {variant.sku}
                      </span>

                      <span className="text-gray-500">
                        Lot prices are added through GRNs
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 px-6 py-5 md:px-8">
          <button
            type="button"
            onClick={
              step === 1
                ? () => navigate("/admindashboard/masters/products")
                : previousStep
            }
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 px-5 hover:bg-gray-50"
          >
            <FiArrowLeft />
            {step === 1 ? "Cancel" : "Previous"}
          </button>

          {step < steps.length ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={uploadingImages}
              className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
              <FiArrowRight />
            </button>
          ) : (
            <button
              type="button"
              disabled={saving || uploadingImages}
              onClick={createProduct}
              className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiCheckCircle />
              {saving ? "Creating…" : "Create Product"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder = "", type = "text" }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">{label}</label>

      <input
        type={type}
        min={type === "number" ? "0" : undefined}
        step={type === "number" ? "0.01" : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">{label}</label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
      >
        {placeholder && <option value="">{placeholder}</option>}

        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <label className="flex cursor-pointer gap-4 rounded-2xl border border-gray-200 p-5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-4 w-4 accent-primary-700"
      />

      <span>
        <span className="block font-medium text-gray-900">{label}</span>

        <span className="mt-1 block text-sm text-gray-500">{description}</span>
      </span>
    </label>
  );
}

function ReviewItem({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-100 p-4">
      <p className="text-xs uppercase text-gray-500">{label}</p>

      <p className="mt-1 font-semibold text-gray-900">{value}</p>
    </div>
  );
}
